/*
 * Auxiliary header records — letting a header show a value that does NOT live
 * on the record it was built from.
 *
 * A detail header is single-record by construction: DetailHeaderCard is given
 * one `moduleName` + `id`, fetches that record's form-groups, and hands the
 * header config a `val(key)` bound to exactly those fields. That is right
 * almost everywhere, but some CONTEXT cards are deliberately rendered against
 * one module while describing a second one. The Quick Submit page is the case
 * that forced this: it renders the JOB header (moduleName="job",
 * customConfigName="submissionJob") on a page whose subject is a CANDIDATE
 * being submitted to that job, so a candidate-owned value such as "where did
 * this candidate come from" was simply unreachable from the header config.
 *
 * Rather than teach the header about candidates (or jobs, or submissions), the
 * host declares any number of EXTRA records it wants resolved alongside the
 * primary one:
 *
 *   <DetailHeaderCard
 *     moduleName="job" customConfigName="submissionJob" id={jobId}
 *     auxRecords={{ candidate: { module: 'candidate', id: applicantId } }}
 *   />
 *
 * and the header config reads them through an extra accessor that mirrors
 * `val`, one level deeper:
 *
 *   buildData({ val, has, field, aux }) → aux('candidate')('source')
 *
 * The HOST-PROP shape was chosen over a config-declared `auxiliary: [{ key,
 * module, idFrom }]` because only the host actually knows the other record's
 * id: on Quick Submit the candidate id is a URL query param, not a field of
 * the job — an `idFrom` pointing into the job's form-groups would have nothing
 * to read. The host already has both ids in hand, so passing them is exact
 * instead of inferred, and DetailHeaderCard stays free of any module-specific
 * routing/lookup knowledge.
 *
 * Everything here FAILS OPEN: an absent prop, a malformed entry, a missing id,
 * a failed request or an unknown key all resolve to `undefined` values. A
 * header must never blank out or throw because a secondary record could not be
 * loaded — it just renders without that part.
 */

// Host prop → a stable, validated list of { key, module, id } fetch requests.
// Entries without a module or without an id are dropped (not fetched): "no id
// yet" is the normal state on a page whose candidate is still being chosen.
export function normalizeAuxRecords(auxRecords) {
  if (!auxRecords || typeof auxRecords !== 'object') return [];
  return Object.entries(auxRecords)
    .map(([key, entry]) => {
      if (!entry || typeof entry !== 'object') return null;
      const module = String(entry.module ?? '').trim();
      const id = entry.id == null ? '' : String(entry.id).trim();
      if (!key || !module || !id) return null;
      return { key: String(key), module, id };
    })
    .filter(Boolean);
}

// Cache/effect key: aux fetches must re-run when a declared record changes, but
// NOT on every render (the prop is usually an inline object literal, so identity
// alone is useless as a dependency).
export function auxRecordsSignature(auxRecords) {
  return normalizeAuxRecords(auxRecords)
    .map(({ key, module, id }) => `${key}:${module}:${id}`)
    .sort()
    .join('|');
}

// field key → value over one fetched form-groups payload. Deliberately simpler
// than headerConfig's primary `val`: no status-default fallback and no
// recordMeta audit stamps, because an auxiliary record is a value source, not
// the subject of the header.
export function auxValueReader(formGroups) {
  const values = new Map();
  for (const group of formGroups ?? []) {
    for (const item of group?.fields ?? []) {
      if (item?.field != null && !values.has(item.field)) values.set(item.field, item.value);
    }
  }
  return (key) => values.get(key);
}

// The `aux` accessor handed to buildData. `aux(key)` always returns a FUNCTION,
// even for a key that was never declared or whose fetch failed, so a header
// config can call aux('candidate')('source') unconditionally without guarding.
export function makeAuxAccessor(groupsByKey) {
  const readers = new Map();
  for (const [key, formGroups] of Object.entries(groupsByKey ?? {})) {
    readers.set(key, auxValueReader(formGroups));
  }
  const missing = () => undefined;
  return (key) => readers.get(key) ?? missing;
}
